<template>
  <b-form @submit="onSubmit" enctype="multipart/form-data">
    <b-card no-body class="radius-top-0">
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <small class="text-gray">
              #{{ oportunidade.id }}
              <span v-if="oportunidade.codigo">
                · {{ oportunidade.codigo }}
              </span>
            </small>
            <div
              class="font-weight-700 text-exitos"
              v-text="oportunidade.nome"
            />
          </div>
          <div class="d-flex flex-row-reverse">
            <b-button type="submit" variant="exitos" :disabled="!canSave">
              Salvar
            </b-button>

            <b-button
              v-if="hasDivergences"
              variant="warning"
              @click="viewDivergences(true)"
            >
              <font-awesome-icon icon="triangle-exclamation" />
              Revisar Divergências
            </b-button>

            <b-button class="btn btn-outline-muted mr-2" @click="goBack">
              Voltar para Listagem
            </b-button>
          </div>
        </div>
      </template>

      <b-overlay variant="light" opacity=".3" blur="2px" :show="loading">
        <b-tabs pills card vertical class="edit-menu-sidebar">
          <b-tab
            title="Dados Básicos"
            :title-item-class="{ 'error-tab': !validations.dados_basicos }"
            active
          >
            <DadosBasicos ref="dados_basicos" />
          </b-tab>

          <b-tab
            title="Concedentes"
            :title-item-class="{ 'error-tab': !validations.concedentes }"
          >
            <Concedentes ref="concedentes" />
          </b-tab>

          <b-tab
            title="Quem Se Destina"
            :title-item-class="{ 'error-tab': !validations.quem_destina }"
          >
            <QuemDestina ref="quem_destina" />
          </b-tab>

          <b-tab
            title="Condições de Participação"
            :title-item-class="{
              'error-tab': !validations.condicao_participacao,
            }"
          >
            <CondicoesParticipacao ref="condicao_participacao" />
          </b-tab>

          <b-tab
            title="Submissão de Propostas"
            :title-item-class="{ 'error-tab': !validations.submeter }"
          >
            <Submeter ref="submeter" />
          </b-tab>

          <b-tab
            title="Cronograma da Oportunidade"
            :title-item-class="{ 'error-tab': !validations.quais_datas }"
          >
            <QuaisDatas ref="quais_datas" />
          </b-tab>

          <b-tab
            title="Valores"
            :title-item-class="{ 'error-tab': !validations.valores }"
          >
            <Valores ref="valores" />
          </b-tab>

          <b-tab
            title="Contrapartidas"
            :title-item-class="{ 'error-tab': !validations.contrapartidas }"
          >
            <Contrapartidas ref="regras_contrapartida" />
          </b-tab>

          <b-tab
            title="Arquivos"
            :title-item-class="{ 'error-tab': !validations.arquivo }"
          >
            <Arquivos ref="arquivo" />
          </b-tab>

          <b-tab
            title="Modelos"
            :title-item-class="{ 'error-tab': !validations.modelo }"
          >
            <Modelos ref="modelo" />
          </b-tab>

          <b-tab
            title="Atualizações"
            :title-item-class="{ 'error-tab': !validations.cronograma }"
          >
            <Cronograma ref="cronograma" />
          </b-tab>

          <b-tab
            title="Resumo"
            :title-item-class="{ 'error-tab': !validations.resumo }"
          >
            <Resumo ref="resumo" />
          </b-tab>
        </b-tabs>
      </b-overlay>
    </b-card>

    <b-modal
      v-model="modalOpened"
      centered
      size="xl"
      body-class="pt-1"
      ok-title="Salvar"
      cancel-title="Cancelar"
      title="Revisar dados divergentes"
      @ok.prevent="submitRevisoes"
      @cancel.prevent="onModalHide"
    >
      <div class="d-flex flex-column">
        <div class="row mb-4">
          <div class="col-4 text-center">
            <b-badge pill variant="info" class="ml-3 px-2"
              >Dados da última extração</b-badge
            >
          </div>
          <div class="col-4 text-center">
            <font-awesome-icon
              icon="arrow-right-arrow-left"
              class="text-gray"
            />
          </div>
          <div class="col-4 text-center">
            <b-badge pill variant="info" class="ml-3 px-2"
              >Dados Atuais</b-badge
            >
          </div>
        </div>

        <div
          class="mb-3"
          v-for="(itens, secao) in camposRevisaoAgrupados"
          :key="secao"
        >
          <collapse-row
            :title="secao"
            :subtitle="subtitulosSecoes[secao]"
            :id="secao"
            :default-active="secao === 'Dados básicos'"
          >
            <div v-for="item in itens" :key="item.id">
              <div class="row pb-4 border-bottom mb-4">
                <div class="col-6">
                  <div
                    class="position-relative p-3"
                    :class="{
                      'border border-success rounded-lg': item.aplicado,
                    }"
                  >
                    <span
                      v-if="item.aplicado"
                      class="position-absolute top-1 right-2 text-success"
                    >
                      <font-awesome-icon icon="circle-check" />
                    </span>
                    <div
                      class="d-flex align-items-center justify-content-between"
                    >
                      <div class="d-block row col-10">
                        <h5>{{ DadosRevisadosCampos[item.campo]?.title }}</h5>
                        <div v-if="item.campo === 'programa_atende_a'">
                          <span
                            v-for="(value, index) in naturezaJuridicaLabels"
                            :key="index"
                            class="badge px-2 badge badge-natureza-juridica badge-pill"
                          >
                            {{ value }}
                          </span>
                        </div>
                        <p
                          v-else-if="
                            DadosRevisadosCampos[item.campo]?.type ===
                            'textarea'
                          "
                          class="mb-0"
                          v-html="
                            getDiffHtml(item.valor_anterior, item.valor_novo)
                          "
                        ></p>
                        <p v-else class="mb-0">
                          {{ item.valor_novo ?? "Sem registros" }}
                        </p>
                      </div>
                      <div>
                        <b-btn
                          v-if="!item.aplicado"
                          size="sm"
                          variant="exitos"
                          @click="proxyClick(item.campo, item.valor_novo)"
                        >
                          Aplicar
                          <font-awesome-icon icon="arrow-right" />
                        </b-btn>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-6">
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <h5>{{ DadosRevisadosCampos[item.campo]?.title }}</h5>
                  </div>
                  <div class="input-w-revert">
                    <el-tooltip
                      class="item"
                      effect="dark"
                      content="Reverter alterações"
                      placement="top"
                    >
                      <b-button
                        class="text-exitos btn-revert"
                        @click="resetToPrevious(item)"
                      >
                        <font-awesome-icon icon="rotate-left" />
                      </b-button>
                    </el-tooltip>
                    <Editor
                      v-if="
                        DadosRevisadosCampos[item.campo]?.type === 'textarea'
                      "
                      v-model="camposRevisaoState[item.campo]"
                      @input="onInputChange(item.campo, item)"
                    />
                    <DateInput
                      v-else-if="
                        DadosRevisadosCampos[item.campo]?.type === 'date'
                      "
                      input-class="form-control"
                      v-model="camposRevisaoState[item.campo]"
                      format="dd/MM/yyyy"
                      @change="onInputChange(item.campo, item)"
                    />
                    <SelectTipoInstrumento
                      v-else-if="item.campo === 'tipo_instrumento'"
                      v-model="camposRevisaoState[item.campo].id"
                    />
                    <SelectTiposInstituicoes
                      v-else-if="item.campo === 'programa_atende_a'"
                      v-model="camposRevisaoState[item.campo]"
                      @options="onOptionsLoaded"
                    />
                    <SelectQualificacaoPropostas
                      v-else-if="item.campo === 'qualificacao_da_proposta'"
                      v-model="camposRevisaoState[item.campo]"
                      :emenda.sync="oportunidade.qualificacao_da_proposta"
                    />
                    <b-form-input
                      v-else
                      v-model="camposRevisaoState[item.campo]"
                      @input="onInputChange(item.campo, item)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </collapse-row>
        </div>
      </div>
    </b-modal>
  </b-form>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { diffWords } from "diff";

import DadosBasicos from "./Form/DadosBasicos.vue";
import Concedentes from "./Form/Concedentes.vue";
import Valores from "./Form/Valores.vue";
import QuemDestina from "./Form/QuemDestina.vue";
import Resumo from "./Form/Resumo.vue";
import QuaisDatas from "./Form/QuaisDatas.vue";
import Contrapartidas from "./Form/Contrapartidas.vue";
import Arquivos from "./Form/Arquivos.vue";
import Modelos from "./Form/Modelos.vue";
import Cronograma from "./Form/Cronograma.vue";
import CondicoesParticipacao from "./Form/CondicoesParticipacao.vue";
import Submeter from "./Form/Submeter.vue";
import DadosRevisadosCampos from "@/helpers/constants";
import Editor from "@/components/Form/Editor";
import DateInput from "@/components/Form/DateInput.vue";
import CAMPOS_SECOES from "../constants";
import SelectTipoInstrumento from "@/modules/oportunidades/Components/SelectTipoInstrumento";
import SelectTiposInstituicoes from "@/modules/tipos_instituicoes/Components/SelectTiposInstituicoes.vue";
import SelectQualificacaoPropostas from "@/modules/oportunidades/Components/Form/SelectQualificacaoPropostas.vue";
import { noop } from "@/helpers/functions/FunctionHelpers.js";

export default {
  components: {
    DadosBasicos,
    Concedentes,
    Valores,
    QuemDestina,
    Resumo,
    QuaisDatas,
    Contrapartidas,
    Arquivos,
    Modelos,
    Cronograma,
    CondicoesParticipacao,
    Submeter,
    Editor,
    DateInput,
    SelectTipoInstrumento,
    SelectTiposInstituicoes,
    SelectQualificacaoPropostas,
  },
  name: "the-form",
  data: () => ({
    modalOpened: false,
    /**@type {Set<any>} */
    handlers: new Set(),
    DadosRevisadosCampos,
    camposRevisaoState: {},
    tiposInstrumentos: [],
    instituicoesTipos: [],
    naturezaJuridicaLabels: [],
    validations: {
      dados_basicos: true,
      concedentes: true,
      valores: true,
      quem_destina: true,
      quais_datas: true,
      contrapartidas: true,
      regras_contrapartida: true,
      arquivo: true,
      modelo: true,
      cronograma: true,
      condicao_participacao: true,
      submeter: true,
      resumo: true,
    },
    subtitulosSecoes: {
      "Dados básicos": "Identificação e descrição principal",
      "Quem se destina": "Público-alvo da oportunidade",
      "Condições de Participação": "Critérios e observações",
      "Cronograma da Oportunidade": "Datas e prazos",
    },
  }),
  computed: {
    ...mapState("oportunidades", [
      "oportunidade",
      "qualificacao_proposta",
      "loading",
    ]),
    camposRevisaoFiltrados() {
      return (this.oportunidade?.campos_revisao || []).filter(
        (item) => item.campo !== "status"
      );
    },
    camposRevisaoAgrupados() {
      const grupos = {};

      if (!this.oportunidade?.campos_revisao) return grupos;

      const ignorarCampos = ["status", "estados_habilitados"];

      let campos_revisao = this.oportunidade.campos_revisao
        .filter((item) => !ignorarCampos.includes(item.campo))
        .filter(
          (item) => !(item.valor_anterior === null && item.valor_novo === null)
        );

      const camposDataInicio = [
        "voluntaria_data_inicio",
        "emenda_data_inicio",
        "data_inicio_beneficiario_especifico",
      ];

      const datasInicio = campos_revisao.filter((item) =>
        camposDataInicio.includes(item.campo)
      );

      if (datasInicio.length > 1) {
        const menor = datasInicio.reduce((min, atual) => {
          const dataMin = new Date(
            min.valor_novo.split("/").reverse().join("-")
          );
          const dataAtual = new Date(
            atual.valor_novo.split("/").reverse().join("-")
          );
          return dataAtual < dataMin ? atual : min;
        });

        campos_revisao = campos_revisao.filter(
          (item) =>
            !camposDataInicio.includes(item.campo) || item.campo === menor.campo
        );
      }

      const camposDataFim = [
        "voluntaria_data_fim",
        "emenda_data_fim",
        "data_fim_beneficiario_especifico",
      ];

      const datasFim = campos_revisao.filter((item) =>
        camposDataFim.includes(item.campo)
      );

      if (datasFim.length > 1) {
        const maior = datasFim.reduce((max, atual) => {
          const dataMax = new Date(
            max.valor_novo.split("/").reverse().join("-")
          );
          const dataAtual = new Date(
            atual.valor_novo.split("/").reverse().join("-")
          );
          return dataAtual > dataMax ? atual : max;
        });

        campos_revisao = campos_revisao.filter(
          (item) =>
            !camposDataFim.includes(item.campo) || item.campo === maior.campo
        );
      }

      for (const item of campos_revisao) {
        const secao = Object.keys(CAMPOS_SECOES).find((secao) =>
          CAMPOS_SECOES[secao].includes(item.campo)
        );
        const secaoNome = secao || "Outros";
        if (!grupos[secaoNome]) grupos[secaoNome] = [];
        grupos[secaoNome].push(item);
      }

      const ordem = [
        "Dados básicos",
        "Quem se destina",
        "Condições de Participação",
        "Cronograma da Oportunidade",
      ];

      const gruposOrdenados = {};
      for (const nome of ordem) {
        if (grupos[nome]) gruposOrdenados[nome] = grupos[nome];
      }

      for (const nome in grupos) {
        if (!ordem.includes(nome)) gruposOrdenados[nome] = grupos[nome];
      }

      return gruposOrdenados;
    },
    hasDivergences() {
      return this.camposRevisaoFiltrados.some((item) => !item.aplicado);
    },
    canSave() {
      return !this.hasDivergences;
    },
  },
  methods: {
    ...mapActions("oportunidades", [
      "ActionSave",
      "ActionSetLoading",
      "ActionGetTiposInstrumentos",
    ]),
    goBack() {
      this.$router.push({ name: "oportunidades_list" });
    },
    async onSubmit(event) {
      event.preventDefault();
      try {
        if (!this.isValid()) {
          this.$notifications.addNotification({
            message: "Os dados do formulário são inválidos",
            type: "danger",
          });
          return;
        }

        await this.ActionSave(this.oportunidade);
        this.$emit("saved", this.oportunidade.id);

        const oportunity = this.oportunidade;

        this.handlers.forEach(
          (handler) => typeof handler === "function" && handler(oportunity)
        );
        this.$notifications.addNotification({
          message: "Registro salvo com sucesso",
          type: "success",
        });
      } catch (error) {
        console.log("error", error);
        this.$notifications.addNotification({
          message: "Erro ao salvar",
          icon: "exclamation-triangle",
          type: "danger",
        });
      }
      this.ActionSetLoading(false);
    },
    isValid() {
      const results = {};
      const refsToCheck = [
        "dados_basicos",
        "valores",
        "quais_datas",
        "quem_destina",
        "regras_contrapartida",
        "arquivo",
        "cronograma",
        "condicao_participacao",
        "submeter",
      ];
      refsToCheck.forEach((refName) => {
        const ref = this.$refs[refName];
        results[refName] = ref?.isValid ? ref.isValid() : true;
      });
      this.validations = { ...this.validations, ...results };
      return Object.values(this.validations).every(Boolean);
    },
    onOptionsLoaded(opcoes) {
      this.instituicoesTipos = opcoes;

      const item = this.oportunidade.campos_revisao.find(
        (c) => c.campo === "programa_atende_a"
      );

      if (item && item.valor_anterior) {
        const valoresMapeados = this.mapearNaturezaJuridicaIds(
          item.valor_anterior
        );
        this.$set(
          this.camposRevisaoState,
          "programa_atende_a",
          valoresMapeados
        );
      }
    },
    viewDivergences(opened) {
      this.modalOpened = opened;
    },
    proxyClick(campo, value) {
      if (!campo) return;

      const campoInfo = this.DadosRevisadosCampos[campo];
      let newValue = value;

      if (campoInfo?.type === "date" && typeof value === "string") {
        const [dia, mes, ano] = value.split("/");
        if (dia && mes && ano) {
          newValue = `${ano}-${mes.padStart(2, "0")}-${dia.padStart(2, "0")}`;
        }
      }

      if (campo === "tipo_instrumento") {
        const tipo = this.tiposInstrumentos.find((t) => t.text === value);
        if (tipo) {
          newValue = { id: tipo.value, label: tipo.text };
        }
      }

      if (campo === "programa_atende_a") {
        newValue = this.mapearNaturezaJuridicaIds(value);
      }

      if (campo === "qualificacao_da_proposta") {
        newValue = this.mapearQualificacaoPropostaIds(value);
      }

      this.$set(this.camposRevisaoState, campo, newValue);

      const item = this.oportunidade.campos_revisao.find(
        (c) => c.campo === campo
      );
      if (item) this.$set(item, "aplicado", true);

      for (const refName in this.$refs) {
        const form = this.$refs[refName];
        if (form && form.form && campo in form.form) {
          form.form[campo] = newValue;
          break;
        }
        if (form && form.model && campo in form.model) {
          form.model[campo] = newValue;
          break;
        }
      }
    },
    onInputChange(campo, item) {
      const normalize = (value) =>
        value
          ?.toString()
          .replace(/<p>|<\/p>/g, "")
          .replace(/&nbsp;/g, " ")
          .replace(/\s+/g, " ")
          .trim() || "";

      if (campo === "tipo_instrumento") {
        const currentId = this.camposRevisaoState[campo]?.id;
        const currentText = this.getTipoNameById(currentId) || "";
        item.aplicado = normalize(currentText) === normalize(item.valor_novo);
        return;
      }

      const valAtual = normalize(this.camposRevisaoState[campo]);
      const valNovo = normalize(item.valor_novo);

      item.aplicado = valAtual === valNovo;
    },
    resetToPrevious(itens) {
      const tipo = this.DadosRevisadosCampos[itens.campo]?.type;

      let valorAnterior = itens.valor_anterior;
      if (tipo === "date" && valorAnterior) {
        const partes = valorAnterior.split("/");
        if (partes.length === 3) {
          valorAnterior = `${partes[2]}-${partes[1].padStart(
            2,
            "0"
          )}-${partes[0].padStart(2, "0")}`;
        }
      }

      if (itens.campo === "tipo_instrumento") {
        const tipo = this.tiposInstrumentos.find(
          (t) => t.text === valorAnterior
        );
        if (tipo) valorAnterior = { id: tipo.value, label: tipo.text };
      }

      if (itens.campo === "programa_atende_a") {
        valorAnterior = this.mapearNaturezaJuridicaIds(valorAnterior);
      }

      this.$set(this.camposRevisaoState, itens.campo, valorAnterior);
      this.$set(itens, "aplicado", false);

      for (const refName in this.$refs) {
        const form = this.$refs[refName];
        if (form && form.form && itens.campo in form.form) {
          form.form[itens.campo] = valorAnterior;
          break;
        }
        if (form && form.model && itens.campo in form.model) {
          form.model[itens.campo] = valorAnterior;
          break;
        }
      }

      this.$notifications.addNotification({
        message: `Campo "${
          DadosRevisadosCampos[itens.campo]?.title
        }" retornado ao anterior.`,
        type: "success",
      });
    },
    getTipoIdByName(nome) {
      const tipo = this.tiposInstrumentos.find(
        (t) =>
          (t.text || "").toString().toLowerCase().trim() ===
          (nome || "").toString().toLowerCase().trim()
      );
      return tipo ? tipo.value : null;
    },
    getTipoNameById(id) {
      const tipo = this.tiposInstrumentos.find((t) => t.value === String(id));
      return tipo ? tipo.text : "";
    },
    updateTabErrors() {
      const newValidations = { ...this.validations };

      Object.keys(newValidations).forEach(
        (key) => (newValidations[key] = true)
      );

      for (const [secao, itens] of Object.entries(
        this.camposRevisaoAgrupados
      )) {
        const hasError = itens.some((item) => !item.aplicado);

        if (hasError) {
          if (secao.includes("Dados básicos"))
            newValidations.dados_basicos = false;
          if (secao.includes("Quem se destina"))
            newValidations.quem_destina = false;
          if (secao.includes("Condições de Participação"))
            newValidations.condicao_participacao = false;
          if (secao.includes("Cronograma")) newValidations.quais_datas = false;
          if (secao.includes("Valores")) newValidations.valores = false;
          if (secao.includes("Contrapartidas"))
            newValidations.contrapartidas = false;
          if (secao.includes("Modelos")) newValidations.modelo = false;
          if (secao.includes("Arquivos")) newValidations.arquivo = false;
          if (secao.includes("Atualizações")) newValidations.cronograma = false;
          if (secao.includes("Submissão")) newValidations.submeter = false;
          if (secao.includes("Concedentes")) newValidations.concedentes = false;
          if (secao.includes("Resumo")) newValidations.resumo = false;
        }
      }

      this.validations = newValidations;
    },
    mapearNaturezaJuridicaIds(value) {
      if (!value) return [];

      let textos = value
        .split(",")
        .map((t) =>
          t
            .trim()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
        )
        .filter(Boolean);

      const substituicoes = {
        "consorcio publico": "consorcios publicos",
        "organizacao da sociedade civil":
          "entidades privadas sem fins lucrativos/osc's",
        "empresa publica/sociedade de economia mista":
          "sociedade de economia mista",
      };

      const textosTratados = textos.map((txt) => {
        const chave = txt.replace(/\s+/g, " ").trim();
        return substituicoes[chave] || chave;
      });

      const textosExpandidos = textosTratados.flatMap((txt) => {
        const match = txt.match(/(.*?)(?:\s+ou\s+do\s+distrito\s+federal)$/i);
        if (match) {
          const base = match[1].trim();
          return [base, base.replace("estadual", "do distrito federal").trim()];
        }
        return [txt];
      });

      if (!Array.isArray(this.instituicoesTipos)) {
        console.warn(
          "this.instituicoesTipos não está definido ou não é um array"
        );
        return [];
      }

      const resultados = textosExpandidos
        .map((txt) => {
          const match = this.instituicoesTipos.find((o) => {
            const nomeNormalizado = o.nome
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "");
            return nomeNormalizado === txt;
          });
          return match ? { id: match.id } : null;
        })
        .filter(Boolean);

      return resultados;
    },
    mapearQualificacaoPropostaIds(value) {
      if (!value) return [];

      const substituicoes = {
        "proposta voluntaria": "voluntaria",
      };

      let textos = value
        .split(",")
        .map((t) =>
          t
            .trim()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
        )
        .filter(Boolean);

      const textosTratados = textos.map((txt) => {
        const chave = txt.replace(/\s+/g, " ").trim();
        return substituicoes[chave] || chave;
      });

      if (!Array.isArray(this.oportunidade.qualificacao_propostas)) {
        console.warn(
          "this.qualificacao_proposta não está definido ou não é um array"
        );
        return [];
      }

      const resultados = textosTratados
        .map((txt) => {
          const match = this.oportunidade.qualificacao_propostas.find((o) => {
            const nomeNormalizado = o
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "");
            return nomeNormalizado === txt;
          });
          return match ? match : null;
        })
        .filter(Boolean);

      return resultados;
    },
    async submitRevisoes() {
      try {
        const campoMap = {
          nome_programa: "nome",
          observacao: "outras_informacoes",
          tipo_instrumento: "tipo_instrumento_id",
          programa_atende_a: "instituicoes_tipos",
        };

        const camposInicio = [
          "voluntaria_data_inicio",
          "emenda_data_inicio",
          "data_inicio_beneficiario_especifico",
        ];

        const camposFim = [
          "voluntaria_data_fim",
          "emenda_data_fim",
          "data_fim_beneficiario_especifico",
        ];

        let menorDataInicio = null;
        let maiorDataFim = null;

        if (this.oportunidade?.campos_revisao?.length) {
          for (const item of this.oportunidade.campos_revisao) {
            const valor = item.valor_novo;
            if (!valor) continue;

            const dataFormatada = (() => {
              if (typeof valor === "string") {
                return valor.includes("-")
                  ? new Date(valor)
                  : new Date(valor.split("/").reverse().join("-"));
              }
              return null;
            })();

            if (camposInicio.includes(item.campo) && dataFormatada) {
              if (!menorDataInicio || dataFormatada < menorDataInicio) {
                menorDataInicio = dataFormatada;
              }
            }

            if (camposFim.includes(item.campo) && dataFormatada) {
              if (!maiorDataFim || dataFormatada > maiorDataFim) {
                maiorDataFim = dataFormatada;
              }
            }
          }
        }

        for (const [campoRevisao, valor] of Object.entries(
          this.camposRevisaoState
        )) {
          const campoOportunidade = campoMap[campoRevisao] || campoRevisao;

          if (campoOportunidade === "tipo_instrumento_id" && valor?.id) {
            this.$set(this.oportunidade, campoOportunidade, valor.id);
          } else if (campoOportunidade in this.oportunidade) {
            this.$set(this.oportunidade, campoOportunidade, valor);
          }
        }

        if (menorDataInicio) {
          const dataIni = menorDataInicio.toISOString().split("T")[0];
          this.$set(this.oportunidade, "data_proposta_ini", dataIni);
        }

        if (maiorDataFim) {
          const dataFim = maiorDataFim.toISOString().split("T")[0];
          this.$set(this.oportunidade, "data_proposta_fim", dataFim);
        }

        this.modalOpened = false;

        await this.ActionSave(this.oportunidade);
        this.$emit("saved", this.oportunidade.id);
        this.$notifications.addNotification({
          message: "Revisões aplicadas com sucesso.",
          type: "success",
        });
      } catch (error) {
        console.error("Erro ao salvar revisões:", error);
        this.$notifications.addNotification({
          message: "Erro ao salvar revisões",
          icon: "exclamation-triangle",
          type: "danger",
        });
      } finally {
        this.ActionSetLoading(false);
      }
    },
    formatNaturezaJuridica() {
      const textos = this.oportunidade?.campos_revisao.find(
        (item) => item.campo === "programa_atende_a"
      );
      if (!textos) return [];

      const textosSeparados = textos.valor_novo
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      this.naturezaJuridicaLabels = textosSeparados.flatMap((txt) => {
        const match = txt.match(/(.*?)(?:\s+ou\s+do\s+distrito\s+federal)$/i);
        if (match) {
          const base = match[1].trim();
          return [
            base,
            base.replace(/estadual/i, "do Distrito Federal").trim(),
          ];
        }
        return [txt];
      });
    },
    getDiffHtml(oldText, newText) {
      const diff = diffWords(oldText || "", newText || "");
      return diff
        .map((part) => {
          if (part.added) {
            return `<span class="badge-diff">${part.value}</span>`;
          } else if (!part.removed) {
            return `<span>${part.value}</span>`;
          } else {
            return "";
          }
        })
        .join("");
    },
    onModalHide() {
      this.$bvModal
        .msgBoxConfirm(
          "Essa operação não poderá ser revertida e os dados revisados não serão salvos.",
          {
            title: "Deseja realmente cancelar a revisão?",
            okTitle: "Sim",
            cancelTitle: "Não",
            modalClass: "modal-custom",
            footerClass: "p-3",
            centered: true,
          }
        )
        .then((value) => {
          this.viewDivergences(!value);
        });
    },
    onSave(handler) {
      if (typeof handler !== "function") return noop;

      this.handlers.add(handler);

      return () => this.handlers.delete(handler);
    },
  },
  async created() {
    try {
      this.tiposInstrumentos = await this.ActionGetTiposInstrumentos();
    } catch (e) {
      this.tiposInstrumentos = [];
      console.error("Erro ao obter tipos instrumento", e);
    }
  },
  mounted() {
    this.$watch(
      () => this.tiposInstrumentos,
      (newList) => {
        if (!newList.length) return;
        for (const campo in this.camposRevisaoState) {
          if (campo === "tipo_instrumento") {
            const current = this.camposRevisaoState[campo];
            if (
              current &&
              typeof current === "object" &&
              !current.id &&
              current.label
            ) {
              const id = this.getTipoIdByName(current.label);
              if (id) {
                this.$set(this.camposRevisaoState, campo, {
                  id,
                  label: current.label,
                });
              }
            }
          }
        }
      },
      { immediate: true }
    );
  },
  provide() {
    return {
      rootForm: {
        onSave: this.onSave,
      },
    };
  },
  watch: {
    oportunidade: {
      immediate: true,
      handler(newVal) {
        if (!newVal?.campos_revisao) return;

        newVal.campos_revisao
          .filter(
            (item) => !["status", "estados_habilitados"].includes(item.campo)
          )
          .forEach((item) => {
            let initialValue = item.valor_anterior || "";
            const tipo = DadosRevisadosCampos[item.campo]?.type;

            if (tipo === "textarea" && initialValue) {
              initialValue = initialValue
                .replace(/^<p>/, "")
                .replace(/<\/p>$/, "");
            }

            if (item.campo === "tipo_instrumento") {
              const id = this.getTipoIdByName(initialValue);
              const label = initialValue;
              initialValue = { id, label };
            }

            if (tipo === "date" && initialValue) {
              const partes = initialValue.split("/");
              if (partes.length === 3) {
                initialValue = `${partes[2]}-${partes[1].padStart(
                  2,
                  "0"
                )}-${partes[0].padStart(2, "0")}`;
              }
            }

            this.formatNaturezaJuridica();
            this.$set(this.camposRevisaoState, item.campo, initialValue);
            this.$set(item, "aplicado", false);
          });

        this.updateTabErrors();
      },
    },
    tiposInstrumentos: {
      immediate: true,
      handler(list) {
        if (!list?.length) return;

        const campoAnterior = this.oportunidade?.campos_revisao?.find(
          (c) => c.campo === "tipo_instrumento"
        );

        if (campoAnterior) {
          let current = this.camposRevisaoState["tipo_instrumento"];
          if (typeof current === "number" || /^\d+$/.test(current)) {
            const tipo = list.find((t) => String(t.value) === String(current));
            if (tipo) {
              current = { id: tipo.value, label: tipo.text };
              this.$set(this.camposRevisaoState, "tipo_instrumento", current);
            }
          }

          if (campoAnterior.valor_anterior) {
            const tipo = list.find(
              (t) => t.text === campoAnterior.valor_anterior
            );
            if (tipo) campoAnterior.aplicado = true;
          }
        }
      },
    },
    camposRevisaoAgrupados: {
      deep: true,
      handler() {
        this.updateTabErrors();
      },
    },
  },
};
</script>

<style lang="scss">
.badge-diff,
.badge-natureza-juridica {
  color: #225373;
  background: #eaeff2;
}

.badge-diff {
  padding: 0 8px;
  border-radius: 5px;
}

.badge-natureza-juridica {
  text-transform: unset;
  font-weight: 600;
}

.input-w-revert {
  position: relative;

  input {
    padding-right: 2.5rem;
  }

  .btn-revert {
    position: absolute;
    top: 2px;
    right: 5px;
    padding: 0.5rem;
    margin: 0;
    background: #fff;
    border-radius: 100%;
    box-shadow: none;
    cursor: pointer;
    z-index: 10;
  }

  &:has(.datepicker) .btn-revert {
    right: 30px;
  }
}

.modal-footer {
  & > * {
    box-shadow: none;
  }

  .btn {
    border-color: #225373;
  }

  .btn-secondary {
    color: #225373;
    border: 1px solid;
  }

  .btn-primary {
    color: #fff;
    background: #225373;
  }
}

.modal-custom {
  .modal-title {
    font-size: 20px;
    font-weight: 600;
    color: #225373;
  }

  .modal-body {
    font-size: 14px;
    font-weight: 400;
    color: #666;
    padding-top: 0;
  }

  .modal-footer {
    background: #f8f8f8;
    .btn {
      padding: 0.625rem 1.5rem;
    }
  }
}
</style>
